/**
 * Where each track can have the procedure (BAR-3000): one list, read by the PN
 * card's Next step and anything else in this repo that offers a location by
 * track. Spec: everself-design-system `15-insurance-project/pn-consultation-card.mdx`,
 * "Eligible locations", as of 2026-10-14; that table is the readable copy, so
 * update both together. Turning a location on or off is a one-line change here.
 *
 * Values are market names: the `market`-coded Location names in
 * `packages/bots/data/market-locations.json`, the vocabulary the Intake and
 * billing Location stores, and what procedure dates are scoped to. The twelve
 * are the procedure centres of `PAC_CENTERS` (`pac-intro-call-script.ts`).
 * Southern California is Temecula: its "Temecula Valley" entry is the market
 * named Temecula in `market-locations.json`. The older Orange County market is
 * the Irvine facility we no longer use, so no track offers it (BAR-3029).
 *
 * bariendo-be's signup coverage results can't import this (the two repos share
 * no package); a change here must be mirrored there by hand.
 */

import { BenefitAdministrator } from './constants';

/** Every centre: where self-pay can have the procedure. */
export const ALL_PROCEDURE_MARKETS = [
  'Boston',
  'Charleston',
  'Charlotte',
  'Dallas',
  'Detroit',
  'Houston',
  'Jacksonville',
  'Miami',
  'Nashville',
  'Tampa',
  'Temecula',
  'Washington DC',
] as const;
export type ProcedureMarket = (typeof ALL_PROCEDURE_MARKETS)[number];

export const ELIGIBLE_LOCATIONS: {
  selfPay: readonly ProcedureMarket[];
  /** The insurance sites, and which states each is the one for. */
  insurance: readonly { market: ProcedureMarket; states: readonly string[] }[];
  /** By benefit administrator. */
  employer: Readonly<Record<BenefitAdministrator, readonly ProcedureMarket[]>>;
} = {
  selfPay: ALL_PROCEDURE_MARKETS,
  insurance: [
    // MUSC.
    { market: 'Charleston', states: ['SC', 'GA'] },
    // Surgeons Choice. Indiana's network statuses are still to confirm (spec open question 5).
    { market: 'Detroit', states: ['MI', 'OH', 'IN'] },
  ],
  employer: {
    // Charleston and DC are in network but off for now.
    [BenefitAdministrator.CARRUM]: ['Charlotte', 'Houston', 'Jacksonville', 'Tampa'],
    // Dallas and DC are MD-approved only, not yet in network.
    [BenefitAdministrator.TRANSCARENT]: [
      'Boston',
      'Charleston',
      'Charlotte',
      'Detroit',
      'Houston',
      'Jacksonville',
      'Miami',
      'Nashville',
      'Tampa',
      'Temecula',
    ],
  },
};

/** The insurance markets, in order. */
export const INSURANCE_MARKETS: readonly ProcedureMarket[] = ELIGIBLE_LOCATIONS.insurance.map((site) => site.market);

/** The insurance site for a state, for the prefill. Undefined for a state no site is for. */
export function insuranceMarketForState(state: string | null | undefined): ProcedureMarket | undefined {
  if (!state) return undefined;
  return ELIGIBLE_LOCATIONS.insurance.find((site) => site.states.includes(state))?.market;
}

/** Where an employer benefit's administrator can have it done; none until the benefit is confirmed. */
export function employerMarkets(administrator: BenefitAdministrator | undefined): readonly ProcedureMarket[] {
  return administrator ? ELIGIBLE_LOCATIONS.employer[administrator] : [];
}
